import React, { useRef, useMemo } from "react";
import styled, { css } from "styled-components";

export type InsertableTag = {
  // Text shown on the toolbar chip, e.g. "Video 1" or "Tip callout".
  label: string;
  // Marker inserted into the content, e.g. "<video#1>".
  tag: string;
};

// Kept as the default so any plain textarea that does not pass its own tags
// behaves exactly as before this editor learned about other resource types.
const DEFAULT_INSERTABLE_TAGS: InsertableTag[] = [
  { label: "<image#1>", tag: "<image#1>" },
  { label: "<image#2>", tag: "<image#2>" },
  { label: "<image#3>", tag: "<image#3>" },
];

type Props = {
  id: string;
  label?: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  error?: any;
  onChange: any;
  onBlur: any;
  insertableTags?: InsertableTag[];
};

function insertAroundSelection(
  el: HTMLTextAreaElement | null,
  before: string,
  after: string = before
) {
  if (!el) return;
  const { selectionStart, selectionEnd, value } = el;
  const selected = value.slice(selectionStart, selectionEnd);
  const next =
    value.slice(0, selectionStart) +
    before +
    selected +
    after +
    value.slice(selectionEnd);
  const cursor =
    selectionStart + before.length + selected.length + after.length;
  return { next, start: cursor, end: cursor };
}

function insertPrefixEachLine(el: HTMLTextAreaElement | null, prefix: string) {
  if (!el) return;
  const { selectionStart, selectionEnd, value } = el;
  const before = value.slice(0, selectionStart);
  const selected = value.slice(selectionStart, selectionEnd);
  const after = value.slice(selectionEnd);

  const block = selected || "";
  const lines = block
    .split("\n")
    .map((l) => (l.startsWith(prefix) ? l : `${prefix}${l}`));
  const replaced = lines.join("\n");

  const next = before + replaced + after;
  const start = selectionStart + prefix.length;
  const end = selectionStart + replaced.length;
  return { next, start, end };
}

function insertAtCursor(el: HTMLTextAreaElement | null, text: string) {
  if (!el) return;
  const { selectionStart, selectionEnd, value } = el;
  const next =
    value.slice(0, selectionStart) + text + value.slice(selectionEnd);
  const cursor = selectionStart + text.length;
  return { next, start: cursor, end: cursor };
}

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
    border-color: var(--color-border-strong);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spacer = styled.span`
  flex: 1;
`;

const InsertLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  padding: 0 var(--space-1);
`;

const TextareaShell = styled.div<{ $error: boolean }>`
  display: flex;
  align-items: stretch;
  background: var(--color-surface);
  border: 1px solid
    ${(p) => (p.$error ? "var(--color-danger)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  padding: var(--space-3);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  &:focus-within {
    border-color: ${(p) =>
      p.$error ? "var(--color-danger)" : "var(--color-accent)"};
    box-shadow: var(--shadow-focus);
  }
`;

const Textarea = styled.textarea`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text);
  line-height: var(--line-height-normal);
  resize: vertical;
  min-height: 200px;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-tertiary);
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const ErrorText = styled.span`
  color: var(--color-danger);
`;

export default function SmallTextEditor({
  id,
  label,
  value,
  disabled,
  placeholder,
  error,
  onChange,
  onBlur,
  insertableTags = DEFAULT_INSERTABLE_TAGS,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const apply = (
    updater?: (
      el: HTMLTextAreaElement | null
    ) => { next: string; start: number; end: number } | void
  ) => {
    if (!ref.current || !updater) return;
    const res = updater(ref.current);
    if (!res) return;
    const { next, start, end } = res;
    const ev = {
      target: { id, name: id, value: next },
    } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
    onChange(ev);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(start, end);
    });
  };

  const counts = useMemo(() => {
    const text = (value || "").replace(/\s+/g, " ").trim();
    return {
      words: text ? text.split(" ").length : 0,
      chars: (value || "").length,
    };
  }, [value]);

  return (
    <Root>
      {label && <Label htmlFor={id}>{label}</Label>}

      <Toolbar>
        <ToolbarButton
          type="button"
          onClick={() => apply((el) => insertAroundSelection(el, "**"))}
          disabled={disabled}
          title="Bold"
        >
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => apply((el) => insertAroundSelection(el, "_"))}
          disabled={disabled}
          title="Italic"
        >
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => apply((el) => insertAroundSelection(el, "`"))}
          disabled={disabled}
          title="Inline code"
        >
          Code
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => apply((el) => insertPrefixEachLine(el, "# "))}
          disabled={disabled}
          title="Heading 1"
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => apply((el) => insertPrefixEachLine(el, "## "))}
          disabled={disabled}
          title="Heading 2"
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() => apply((el) => insertPrefixEachLine(el, "- "))}
          disabled={disabled}
          title="Bulleted list"
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          type="button"
          onClick={() =>
            apply((el) => insertAroundSelection(el, "[", "](https://)"))
          }
          disabled={disabled}
          title="Link"
        >
          Link
        </ToolbarButton>

        {insertableTags.length > 0 && <Spacer />}
        {insertableTags.length > 0 && <InsertLabel>Insert</InsertLabel>}
        {insertableTags.map((t) => (
          <ToolbarButton
            key={t.tag}
            type="button"
            onClick={() => apply((el) => insertAtCursor(el, `\n\n${t.tag}\n\n`))}
            disabled={disabled}
            title={`Insert ${t.tag}`}
          >
            {t.label}
          </ToolbarButton>
        ))}
      </Toolbar>

      <TextareaShell $error={Boolean(error)}>
        <Textarea
          id={id}
          name={id}
          ref={ref}
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          rows={12}
          spellCheck={true}
        />
      </TextareaShell>

      <Meta>
        <span>
          {counts.words} words · {counts.chars} chars
        </span>
        {error ? <ErrorText>{String(error)}</ErrorText> : <span />}
      </Meta>
    </Root>
  );
}
